/**
 * API конструктора шаблонов карточек знака Визуализатора.
 */

import { del, get, post, put } from "@/lib/api-client-v2";
import type {
  VisualizerSignCardTemplateListResponse,
  VisualizerSignCardTemplateWithDetails,
  VisualizerSignCardFieldType,
} from "@/types/visualizer-sign-card-template";
import type {
  CardFieldWidgetTypeItem,
  CardTemplateImportResponse,
  CardTemplatePublishRequest,
  CardTemplatePublishResponse,
  CardTemplateRestoreRequest,
} from "@/types/card-template";

export interface VisualizerSignCardTemplateCreatePayload {
  code: string;
  name: string;
  description?: string | null;
  symbol_kls_id?: number | null;
  organization_kls_id?: number | null;
  is_default?: boolean;
}

export interface VisualizerSignCardTemplateUpdatePayload {
  name?: string;
  description?: string | null;
  symbol_kls_id?: number | null;
  organization_kls_id?: number | null;
  is_default?: boolean;
  is_active?: boolean;
}

export interface VisualizerSignCardSectionPayload {
  code: string;
  name: string;
  display_order?: number;
  is_collapsible?: boolean;
  is_default_open?: boolean;
  ui_config?: Record<string, unknown>;
}

export interface VisualizerSignCardSectionUpdatePayload {
  code?: string;
  name?: string;
  display_order?: number;
  is_collapsible?: boolean;
  is_default_open?: boolean;
  ui_config?: Record<string, unknown>;
}

export interface VisualizerSignCardFieldPayload {
  field_key: string;
  label: string;
  description?: string | null;
  section_id?: number | null;
  field_type: VisualizerSignCardFieldType;
  qual_id: number;
  field_type_kls_id?: number | null;
  kls_id?: number | null;
  is_required?: boolean;
  is_readonly?: boolean;
  display_order?: number;
  is_active?: boolean;
}

export interface VisualizerSignCardFieldUpdatePayload {
  field_key?: string;
  label?: string;
  description?: string | null;
  section_id?: number | null;
  field_type?: VisualizerSignCardFieldType;
  qual_id?: number;
  field_type_kls_id?: number | null;
  kls_id?: number | null;
  is_required?: boolean;
  is_readonly?: boolean;
  display_order?: number;
  is_active?: boolean;
}

export const visualizerSignCardApi = {
  listFieldWidgetTypes: () =>
    get<CardFieldWidgetTypeItem[]>("/visualizer-sign-card-templates/meta/field-widget-types"),

  list: (params?: {
    status?: string;
    symbol_kls_id?: number;
    global_type_only?: boolean;
    is_active?: boolean;
    skip?: number;
    limit?: number;
  }) => get<VisualizerSignCardTemplateListResponse>("/visualizer-sign-card-templates", params ?? {}),

  get: (id: number) => get<VisualizerSignCardTemplateWithDetails>(`/visualizer-sign-card-templates/${id}`),

  create: (body: VisualizerSignCardTemplateCreatePayload) =>
    post<VisualizerSignCardTemplateWithDetails>("/visualizer-sign-card-templates", body),

  update: (id: number, body: VisualizerSignCardTemplateUpdatePayload) =>
    put<VisualizerSignCardTemplateWithDetails>(`/visualizer-sign-card-templates/${id}`, body),

  publish: (id: number, body?: CardTemplatePublishRequest) =>
    post<CardTemplatePublishResponse>(`/visualizer-sign-card-templates/${id}/publish`, body ?? {}),

  archive: (id: number, body?: CardTemplatePublishRequest) =>
    post<CardTemplatePublishResponse>(`/visualizer-sign-card-templates/${id}/archive`, body ?? {}),

  restore: (id: number, body?: CardTemplateRestoreRequest) =>
    post<CardTemplatePublishResponse>(`/visualizer-sign-card-templates/${id}/restore`, body ?? {}),

  delete: (id: number) => del<void>(`/visualizer-sign-card-templates/${id}`),

  clone: (templateId: number, newCode: string) =>
    post<VisualizerSignCardTemplateWithDetails>(
      `/visualizer-sign-card-templates/${templateId}/clone?new_code=${encodeURIComponent(newCode)}`,
      {},
    ),

  /** JSON-пакет schema geopulseml.visualizer_sign_card_template_export v1 */
  exportTemplateJson: (templateId: number) =>
    get<Record<string, unknown>>(`/visualizer-sign-card-templates/${templateId}/export`),

  exportTemplatesBulk: (templateIds: number[]) =>
    post<Record<string, unknown>>("/visualizer-sign-card-templates/export", {
      template_ids: templateIds,
    }),

  importTemplatesJson: (
    bundle: Record<string, unknown>,
    onConflict: "error" | "skip" | "suffix" = "error",
  ) => {
    const q = new URLSearchParams({ on_conflict: onConflict });
    return post<CardTemplateImportResponse>(
      `/visualizer-sign-card-templates/import?${q.toString()}`,
      bundle,
    );
  },

  addSection: (templateId: number, body: VisualizerSignCardSectionPayload) =>
    post<VisualizerSignCardTemplateWithDetails>(`/visualizer-sign-card-templates/${templateId}/sections`, body),

  updateSection: (templateId: number, sectionId: number, body: VisualizerSignCardSectionUpdatePayload) =>
    put<VisualizerSignCardTemplateWithDetails>(`/visualizer-sign-card-templates/${templateId}/sections/${sectionId}`, body),

  deleteSection: (templateId: number, sectionId: number) =>
    del<VisualizerSignCardTemplateWithDetails>(`/visualizer-sign-card-templates/${templateId}/sections/${sectionId}`),

  addField: (templateId: number, body: VisualizerSignCardFieldPayload) =>
    post<VisualizerSignCardTemplateWithDetails>(`/visualizer-sign-card-templates/${templateId}/fields`, body),

  updateField: (templateId: number, fieldId: number, body: VisualizerSignCardFieldUpdatePayload) =>
    put<VisualizerSignCardTemplateWithDetails>(`/visualizer-sign-card-templates/${templateId}/fields/${fieldId}`, body),

  deleteField: (templateId: number, fieldId: number) =>
    del<VisualizerSignCardTemplateWithDetails>(`/visualizer-sign-card-templates/${templateId}/fields/${fieldId}`),
};
